/**
 * Local mouth-open detector using MediaPipe Face Landmarker.
 *
 * Assets are served from /public (WASM + .task) — no CDN at runtime.
 * Shares the same MediaStream as Camera Kit via a hidden (but still decoded)
 * <video>. Runs on CPU so Camera Kit keeps exclusive use of WebGL.
 * Fires once when the mouth opens long enough.
 */

import { FaceLandmarker, FilesetResolver } from '@mediapipe/tasks-vision';
import type { FaceLandmarkerResult } from '@mediapipe/tasks-vision';
import { publicPath } from '../lib/publicPath';
import { devLog, devWarn } from '../lib/devLog';

const TAG = '[mouth]';

const WASM_PATH = publicPath('mediapipe/wasm');
const MODEL_PATH = publicPath('models/face_landmarker.task');

/**
 * jawOpen blendshape — the model's own name for this signal.
 *
 * There is no `mouthOpen` category. face_landmarker.task emits the 52 ARKit
 * shapes (jawOpen, mouthClose, mouthPucker, mouthFunnel, …) and asking it for a
 * name it does not publish scored every frame at zero, so this half of the
 * detector has never contributed anything on any device and the landmark ratio
 * below has been carrying it alone.
 *
 * A caw sits well above 0.6; ordinary speech peaks around 0.3.
 */
const JAW_OPEN_THRESHOLD = 0.4;
/**
 * Inner-lip gap over mouth width, in PIXELS — see mouthOpenLandmarkRatio for
 * why the axes have to be un-normalised first. ~0.05 closed, 0.35+ is
 * unmistakably open.
 */
const LANDMARK_RATIO_THRESHOLD = 0.35;
const OPEN_FRAMES_REQUIRED = 2;
const COOLDOWN_MS = 2000;

/** Minimum gap between inferences. ~15Hz — see the gate in `tick()`. */
const DETECT_INTERVAL_MS = 66;

/** Face mesh landmark indices (MediaPipe Face Landmarker). */
const UPPER_LIP = 13;
const LOWER_LIP = 14;
const MOUTH_LEFT = 61;
const MOUTH_RIGHT = 291;

export interface MouthOpenDetector {
  start(stream: MediaStream, onMouthOpen: () => void): Promise<void>;
  stop(): void;
}

let landmarkerPromise: Promise<FaceLandmarker> | null = null;

export function warmMouthDetector(): Promise<FaceLandmarker> {
  if (landmarkerPromise) return landmarkerPromise;

  landmarkerPromise = (async () => {
    const t0 = performance.now();
    devLog(`${TAG} loading local WASM + model`);
    const vision = await FilesetResolver.forVisionTasks(WASM_PATH);

    // CPU only — Camera Kit owns WebGL. A GPU FaceLandmarker creates a second
    // WebGL context and routinely triggers "context lost" + LensAbortError
    // (especially Safari / WebKit) when both warm or run together.
    const landmarker = await FaceLandmarker.createFromOptions(vision, {
      baseOptions: { modelAssetPath: MODEL_PATH, delegate: 'CPU' },
      runningMode: 'VIDEO',
      numFaces: 1,
      outputFaceBlendshapes: true,
      outputFacialTransformationMatrixes: false,
    });
    devLog(`${TAG} ready (CPU) in ${Math.round(performance.now() - t0)}ms`);
    return landmarker;
  })().catch((err) => {
    landmarkerPromise = null;
    throw err;
  });

  return landmarkerPromise;
}

/**
 * Timestamps for MediaPipe's VIDEO mode, monotonic for the life of the page.
 *
 * This is deliberately module-level, not per-detector. `warmMouthDetector`
 * caches one FaceLandmarker and every capture shares it, so its graph requires
 * strictly increasing timestamps across all of them. A per-detector counter
 * starting at zero fed the shared graph a timestamp from the past on the second
 * capture, and it then rejected every single frame with "Packet timestamp
 * mismatch" — the detector never fired again after a retake, so the guest cawed
 * and nothing happened until the 35s fallback appeared.
 *
 * performance.now() is monotonic by construction; the +1 guards the case where
 * two frames land inside the same millisecond, since equal is not increasing.
 */
let lastTimestampMs = 0;
function nextTimestampMs(): number {
  const now = performance.now();
  lastTimestampMs = now > lastTimestampMs ? now : lastTimestampMs + 1;
  return lastTimestampMs;
}

export function createMouthOpenDetector(): MouthOpenDetector {
  let video: HTMLVideoElement | null = null;
  let rafId: number | null = null;
  let running = false;
  let openStreak = 0;
  let lastFireAt = 0;
  let lastVideoTime = -1;
  let onMouthOpen: (() => void) | null = null;
  let lastDebugAt = 0;
  let lastInferenceAt = 0;

  function tick(landmarker: FaceLandmarker): void {
    if (!running || !video) return;

    /*
     * Wall-clock gate, sitting outside the currentTime logic below rather than
     * replacing it.
     *
     * rAF fires at the display's 60Hz against a 30fps camera, so inference ran
     * about twice per camera frame — the second run on pixels it had already
     * seen. Each run is not just CPU: MediaPipe holds its own WebGL context and
     * pulls the frame back off the GPU, and that readback contends with the
     * recorder's canvas capture. On the kiosk that contention is what takes the
     * live preview apart, so halving the readback rate is worth more here than
     * the inference time it saves.
     *
     * Deliberately NOT expressed through `lastVideoTime`: the `||` on line ~121
     * is there so a stalled `currentTime` still gets inference, and gating on
     * the clock instead keeps that property intact — a stalled video still runs,
     * just at this rate.
     *
     * 15Hz is comfortably fast for the trigger: OPEN_FRAMES_REQUIRED is 2, so a
     * mouth must be open for ~133ms, against a caw of half a second or more.
     */
    const now = performance.now();
    if (now - lastInferenceAt < DETECT_INTERVAL_MS) {
      rafId = requestAnimationFrame(() => tick(landmarker));
      return;
    }
    lastInferenceAt = now;

    // display:none videos often never advance currentTime — we keep the element
    // off-screen with real pixel size instead. Still guard on readyState.
    if (video.readyState >= 2) {
      const t = video.currentTime;
      // Even if currentTime stalls, keep trying with a monotonic timestamp.
      if (t !== lastVideoTime || video.videoWidth > 0) {
        lastVideoTime = t;
        let result: FaceLandmarkerResult;
        try {
          result = landmarker.detectForVideo(video, nextTimestampMs());
        } catch (err) {
          devWarn(`${TAG} detectForVideo error`, err);
          rafId = requestAnimationFrame(() => tick(landmarker));
          return;
        }

        const jaw = jawOpenBlendshape(result);
        const ratio = mouthOpenLandmarkRatio(result, video.videoWidth, video.videoHeight);
        const open = jaw >= JAW_OPEN_THRESHOLD || ratio >= LANDMARK_RATIO_THRESHOLD;

        const now = Date.now();
        if (now - lastDebugAt > 1000) {
          lastDebugAt = now;
          const faces = result.faceLandmarks?.length ?? 0;
          // Frame size is in here because the ratio is only meaningful against
          // it — if this ever looks wrong on a device, that is the first number
          // to read.
          devLog(
            `${TAG} ${video.videoWidth}x${video.videoHeight} faces=${faces} jaw=${jaw.toFixed(2)} ratio=${ratio.toFixed(2)} open=${open}`,
          );
        }

        if (open) {
          openStreak += 1;
          if (openStreak >= OPEN_FRAMES_REQUIRED && now - lastFireAt > COOLDOWN_MS) {
            lastFireAt = now;
            openStreak = 0;
            devLog(
              `${TAG} mouth open (jaw=${jaw.toFixed(2)} ratio=${ratio.toFixed(2)})`,
            );
            onMouthOpen?.();
          }
        } else {
          openStreak = 0;
        }
      }
    }

    rafId = requestAnimationFrame(() => tick(landmarker));
  }

  return {
    async start(stream: MediaStream, callback: () => void): Promise<void> {
      onMouthOpen = callback;
      openStreak = 0;
      lastFireAt = 0;
      lastVideoTime = -1;
      lastDebugAt = 0;
      // NB: no timestamp reset here — see nextTimestampMs.

      const landmarker = await warmMouthDetector();

      const track = stream.getVideoTracks()[0];
      const settings = track?.getSettings() ?? {};
      const w = settings.width ?? 640;
      const h = settings.height ?? 480;

      // Must NOT use display:none — browsers skip decoding those frames.
      // Keep a real-sized off-screen video so MediaPipe gets pixels.
      video = document.createElement('video');
      video.srcObject = stream;
      video.muted = true;
      video.playsInline = true;
      video.setAttribute('playsinline', 'true');
      video.width = w;
      video.height = h;
      Object.assign(video.style, {
        position: 'fixed',
        left: '0',
        top: '0',
        width: `${w}px`,
        height: `${h}px`,
        opacity: '0',
        pointerEvents: 'none',
        zIndex: '-1',
      });
      document.body.appendChild(video);
      await video.play();

      running = true;
      rafId = requestAnimationFrame(() => tick(landmarker));
      devLog(`${TAG} watching stream (${w}x${h})`);
    },

    stop(): void {
      running = false;
      onMouthOpen = null;
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
      if (video) {
        video.pause();
        video.srcObject = null;
        video.parentElement?.removeChild(video);
        video = null;
      }
      devLog(`${TAG} stopped`);
    },
  };
}

function jawOpenBlendshape(result: FaceLandmarkerResult): number {
  const cats = result.faceBlendshapes?.[0]?.categories;
  if (!cats?.length) return 0;
  const jaw =
    cats.find((c) => c.categoryName === 'jawOpen') ??
    cats.find((c) => c.displayName === 'jawOpen');
  return jaw?.score ?? 0;
}

/**
 * Inner-lip gap as a fraction of mouth width.
 *
 * Landmarks come back normalised per axis — x against frame width, y against
 * frame height — so the two are only comparable on a square frame. Dividing a
 * mostly-vertical distance by a mostly-horizontal one therefore multiplies the
 * answer by the frame's aspect ratio, and the same caw reads about 1.78 on a
 * 1280x720 laptop webcam and about 0.56 on a 720x1280 phone: a factor of three
 * between the device this was tuned on and the device guests actually use. On a
 * phone the threshold was simply unreachable, so nothing fired and the guest sat
 * there cawing until the 35s fallback button appeared.
 *
 * Scaling each axis back up by the frame size puts both in pixels, where the
 * ratio means the same thing on any camera.
 */
function mouthOpenLandmarkRatio(
  result: FaceLandmarkerResult,
  frameWidth: number,
  frameHeight: number,
): number {
  const lm = result.faceLandmarks?.[0];
  if (!lm || lm.length <= MOUTH_RIGHT) return 0;
  if (!frameWidth || !frameHeight) return 0;
  const upper = lm[UPPER_LIP];
  const lower = lm[LOWER_LIP];
  const left = lm[MOUTH_LEFT];
  const right = lm[MOUTH_RIGHT];
  if (!upper || !lower || !left || !right) return 0;
  const vertical = Math.hypot(
    (upper.x - lower.x) * frameWidth,
    (upper.y - lower.y) * frameHeight,
  );
  const width = Math.hypot(
    (left.x - right.x) * frameWidth,
    (left.y - right.y) * frameHeight,
  );
  if (width < 1e-6) return 0;
  return vertical / width;
}
